import { useState } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, Switch, message, Upload, Progress, Card
} from 'antd';
import { PlusOutlined, UploadOutlined, RocketOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentVersionsApi } from '../api/agents';
import type { AgentVersion, CreateVersionRequest } from '../types';

export function AgentVersions() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<{
    downloadUrl: string;
    checksum: string;
    fileSize: number;
  } | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: versions = [], isLoading } = useQuery({
    queryKey: ['agent-versions'],
    queryFn: agentVersionsApi.getAll,
  });

  const { data: latestVersion } = useQuery({
    queryKey: ['agent-versions', 'latest'],
    queryFn: agentVersionsApi.getLatest,
  });

  const createMutation = useMutation({
    mutationFn: agentVersionsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-versions'] });
      message.success('Versiyon oluşturuldu');
      setIsModalOpen(false);
      form.resetFields();
      setUploadedFile(null);
    },
    onError: () => message.error('Versiyon oluşturulamadı'),
  });

  const handleUpload = async (file: File) => {
    const version = form.getFieldValue('version');
    if (!version) {
      message.error('Önce versiyon numarasını girin');
      return false;
    }

    try {
      setUploadProgress(0);
      // Simulate progress for better UX
      const interval = setInterval(() => {
        setUploadProgress(p => Math.min(p + 10, 90));
      }, 200);

      const result = await agentVersionsApi.upload(file, version);

      clearInterval(interval);
      setUploadProgress(100);

      setUploadedFile(result);
      form.setFieldsValue({
        downloadUrl: result.downloadUrl,
        checksum: result.checksum,
        fileSize: result.fileSize,
      });

      message.success('Dosya yüklendi');
    } catch {
      message.error('Dosya yüklenemedi');
      setUploadProgress(0);
    }

    return false; // Prevent default upload behavior
  };

  const handleSubmit = (values: CreateVersionRequest) => {
    createMutation.mutate({
      ...values,
      fileSize: uploadedFile?.fileSize || 0,
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const columns = [
    {
      title: 'Versiyon',
      dataIndex: 'version',
      key: 'version',
      render: (version: string, record: AgentVersion) => (
        <Space>
          <Tag color="blue">{version}</Tag>
          {latestVersion?.id === record.id && (
            <Tag color="green" icon={<CheckCircleOutlined />}>En Güncel</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Dosya Boyutu',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size: number) => formatFileSize(size),
    },
    {
      title: 'Zorunlu',
      dataIndex: 'isMandatory',
      key: 'isMandatory',
      render: (isMandatory: boolean) => (
        <Tag color={isMandatory ? 'red' : 'default'}>
          {isMandatory ? 'Zorunlu' : 'Opsiyonel'}
        </Tag>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'Yayın Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString('tr-TR'),
    },
    {
      title: 'Notlar',
      dataIndex: 'releaseNotes',
      key: 'releaseNotes',
      ellipsis: true,
      render: (notes: string | null) => notes || '-',
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>Agent Versiyonları</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setUploadedFile(null);
            setUploadProgress(0);
            setIsModalOpen(true);
          }}
        >
          Yeni Versiyon
        </Button>
      </div>

      {latestVersion && (
        <Card style={{ marginBottom: 16 }}>
          <Space>
            <RocketOutlined style={{ fontSize: 24, color: '#1890ff' }} />
            <div>
              <strong>Güncel Versiyon:</strong> {latestVersion.version}
              <br />
              <small style={{ color: '#666' }}>
                {new Date(latestVersion.createdAt).toLocaleString('tr-TR')} tarihinde yayınlandı
              </small>
            </div>
          </Space>
        </Card>
      )}

      <Table
        columns={columns}
        dataSource={versions}
        rowKey="id"
        loading={isLoading}
        locale={{ emptyText: 'Henüz versiyon bulunmuyor' }}
      />

      <Modal
        title="Yeni Versiyon Yayınla"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setUploadedFile(null);
          setUploadProgress(0);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="version"
            label="Versiyon Numarası"
            rules={[
              { required: true, message: 'Versiyon numarası gerekli' },
              { pattern: /^\d+\.\d+\.\d+$/, message: 'Format: X.Y.Z (örn: 1.0.0)' }
            ]}
          >
            <Input placeholder="1.0.0" />
          </Form.Item>

          <Form.Item label="Kurulum Dosyası" required>
            <Upload.Dragger
              accept=".exe"
              showUploadList={false}
              beforeUpload={handleUpload}
              disabled={!form.getFieldValue('version')}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">
                Setup dosyasını sürükleyin veya tıklayarak seçin
              </p>
              <p className="ant-upload-hint">
                Önce versiyon numarasını girin
              </p>
            </Upload.Dragger>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <Progress percent={uploadProgress} style={{ marginTop: 8 }} />
            )}

            {uploadedFile && (
              <div style={{ marginTop: 8, padding: 8, background: '#f6ffed', borderRadius: 4 }}>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                Dosya yüklendi: {formatFileSize(uploadedFile.fileSize)}
              </div>
            )}
          </Form.Item>

          <Form.Item name="downloadUrl" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="checksum" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="fileSize" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="releaseNotes"
            label="Yayın Notları"
          >
            <Input.TextArea
              rows={4}
              placeholder="Bu versiyonda yapılan değişiklikler..."
            />
          </Form.Item>

          <Form.Item
            name="isMandatory"
            label="Zorunlu Güncelleme"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="notifyAgents"
            label="Agentları Bilgilendir"
            valuePropName="checked"
            initialValue={true}
            extra="Açık olursa, tüm bağlı agentlara güncelleme bildirimi gönderilir"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending}
                disabled={!uploadedFile}
              >
                Yayınla
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
